import React, { useState } from "react";
import * as XLSX from "xlsx";
import {
    getFirestore, collection, writeBatch, doc, getDoc, Timestamp, deleteDoc
} from "firebase/firestore";
import Swal from "sweetalert2";
import db from "./Firebase";

function BatchUploadUsers() {
    const [file, setFile] = useState(null);
    const [records, setRecords] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [mode, setMode] = useState(""); // 'upload' or 'update'

    const handleFileChange = (e) => {
        const f = e.target.files[0];
        setFile(f);

        const reader = new FileReader();
        reader.onload = (evt) => {
            const data = new Uint8Array(evt.target.result);
            const workbook = XLSX.read(data, { type: "array", cellDates: true });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(sheet);
            setRecords(rows);
        };
        reader.readAsArrayBuffer(f);
    };

    const toTimestamp = (val) => {
        if (!val) return null;
        if (val instanceof Date) return Timestamp.fromDate(val);
        // Handle DD/MM/YYYY or DD-MM-YYYY (common Indian format)
        const dmyMatch = String(val).match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
        if (dmyMatch) {
            const d = new Date(`${dmyMatch[3]}-${dmyMatch[2].padStart(2, "0")}-${dmyMatch[1].padStart(2, "0")}`);
            return isNaN(d) ? null : Timestamp.fromDate(d);
        }
        const d = new Date(val);
        return isNaN(d) ? null : Timestamp.fromDate(d);
    };

    const handleUpload = async () => {
        if (!records.length) {
            Swal.fire("Error", "No records to upload", "error");
            return;
        }
        setUploading(true);
        const dbInstance = getFirestore();
        const batch = writeBatch(dbInstance);

        let successCount = 0;
        let failCount = 0;

        for (let r of records) {
            try {
                const phone = String(
                    r["phone_no"] || r["Phone No"] || r["Phone"] || r["phone"] || ""
                ).trim();
                if (!phone) {
                    console.log("Row keys:", Object.keys(r));
                    console.log("Row phone:", r["phone_no"], r["phone"]);
                    failCount++;
                    continue;
                }

                // Prepare data objects
                const funderData = {
                    phone_no: phone,
                    name: r["name"] || "",
                    username: r["username"] || "",
                    state: r["state"] || "",
                    city: r["city"] || "",
                    type: r["type"] || "",
                    age_range: r["age"] || "",
                    start_date: toTimestamp(r["start_date"]),
                    end_date: toTimestamp(r["end_date"]),
                    funder: r["funder"] || "",
                    phase: String(r["phase"] || ""),
                };

                const featureData = {
                    end_date: funderData.end_date,
                    funder: funderData.funder,
                    phase: `phase ${funderData.phase}`,
                    phone: phone,
                    start_date: funderData.start_date,
                    status: "1",
                };

                // Document references
                const docRefUsers = doc(collection(dbInstance, "funder_users_list"), phone);
                const docRefFeatures = doc(collection(dbInstance, "udhyogini_features"), phone);

                batch.set(docRefUsers, funderData, { merge: true });
                batch.set(docRefFeatures, featureData, { merge: true });

                successCount++;
            } catch (err) {
                console.error("Batch row error:", err, r);
                failCount++;
            }
        }

        try {
            await batch.commit();
            Swal.fire("Success", `Uploaded ${successCount} records, failed ${failCount}`, "success");
        } catch (err) {
            console.error("Batch commit error:", err);
            Swal.fire("Error", "Batch upload failed", "error");
        } finally {
            setUploading(false);
        }
    };



    const handleUpdate = async () => {
        if (!records.length) {
            Swal.fire("Error", "No records to update", "error");
            return;
        }

        setUploading(true);
        const dbInstance = getFirestore();
        const batch = writeBatch(dbInstance);

        let successCount = 0;
        let failCount = 0;

        for (let r of records) {
            try {
                const oldPhone = String(r["old_phone_no"] || "").trim();
                const newPhone = String(r["phone"] || "").trim();

                if (!oldPhone || !newPhone) {
                    console.warn("Missing old or new phone number:", r);
                    failCount++;
                    continue;
                }

                // Delete old documents
                const oldUserRef = doc(dbInstance, "funder_users_list", oldPhone);
                const oldFeatureRef = doc(dbInstance, "udhyogini_features", oldPhone);

                // Optional: check if doc exists before deletion (not required if you’re okay skipping silently)
                const oldDocSnap = await getDoc(oldUserRef);
                if (oldDocSnap.exists()) {
                    await deleteDoc(oldUserRef);
                    await deleteDoc(oldFeatureRef);
                } else {
                    console.log(`Old phone not found: ${oldPhone} (skipping delete)`);
                }

                // Now create new doc with `phone`
                const funderData = {
                    phone_no: newPhone,
                    name: r["name"] || "",
                    username: r["username"] || "",
                    state: r["state"] || "",
                    city: r["city"] || "",
                    type: r["type"] || "",
                    age_range: r["age"] || "",
                    start_date: toTimestamp(r["start_date"]),
                    end_date: toTimestamp(r["end_date"]),
                    funder: r["funder"] || "",
                    phase: String(r["phase"] || ""),
                };

                const featureData = {
                    end_date: funderData.end_date,
                    funder: funderData.funder,
                    phase: `phase ${funderData.phase}`,
                    phone: newPhone,
                    start_date: funderData.start_date,
                    status: "1",
                };

                const newUserRef = doc(dbInstance, "funder_users_list", newPhone);
                const newFeatureRef = doc(dbInstance, "udhyogini_features", newPhone);

                batch.set(newUserRef, funderData, { merge: true });
                batch.set(newFeatureRef, featureData, { merge: true });

                successCount++;
            } catch (err) {
                console.error("Error updating row:", err, r);
                failCount++;
            }
        }

        try {
            await batch.commit();
            Swal.fire("Success", `Updated ${successCount} records, Failed ${failCount}`, "success");
        } catch (err) {
            console.error("Batch commit error:", err);
            Swal.fire("Error", "Batch update failed", "error");
        } finally {
            setUploading(false);
        }
    };


    const sampleNewUserLink = "/files//upload_new_users.xlsx";
    const sampleUpdateUserLink = "/files/update_old_users.xlsx";

    // return (
    //     <div className="container p-4">
    //         <h3>Excel Importer</h3>
    //         <div className="d-flex gap-3 mb-4">
    //             <button
    //                 className="btn btn-success"
    //                 onClick={() => {
    //                     setMode("upload");
    //                     setFile(null);
    //                     setRecords([]);
    //                 }}
    //             >
    //                 Upload New Users
    //             </button>
    //             <a href={sampleNewUserLink} download className="btn btn-outline-primary">
    //                 📥 Sample Excel for New Users
    //             </a>

    //             <button
    //                 className="btn btn-warning"
    //                 onClick={() => {
    //                     setMode("update");
    //                     setFile(null);
    //                     setRecords([]);
    //                 }}
    //             >
    //                 Update Existing Users
    //             </button>
    //             <a href={sampleUpdateUserLink} download className="btn btn-outline-primary">
    //                 📥 Sample Excel for Update
    //             </a>
    //         </div>

    //         {mode && (
    //             <>
    //                 <span>{mode}</span>
    //                 <div className="mb-3">
    //                     <input type="file" accept=".xls,.xlsx,.csv" onChange={handleFileChange} />
    //                 </div>

    //                 {records.length > 0 && (
    //                     <>
    //                         <div className="table-responsive mb-3">
    //                             <table className="table table-bordered table-sm">
    //                                 <thead>
    //                                     <tr>
    //                                         {Object.keys(records[0]).map((key) => (
    //                                             <th key={key}>{key}</th>
    //                                         ))}
    //                                     </tr>
    //                                 </thead>
    //                                 <tbody>
    //                                     {records.map((row, idx) => (
    //                                         <tr key={idx}>
    //                                             {Object.values(row).map((value, i) => (
    //                                                 <td key={i}>{value}</td>
    //                                             ))}
    //                                         </tr>
    //                                     ))}
    //                                 </tbody>
    //                             </table>
    //                         </div>

    //                         <button
    //                             className="btn btn-primary"
    //                             onClick={handleUploadOrUpdate}
    //                             disabled={uploading}
    //                         >
    //                             {uploading
    //                                 ? `${mode === "upload" ? "Uploading..." : "Updating..."}`
    //                                 : mode === "upload"
    //                                     ? "Submit New Users"
    //                                     : "Update Users"}
    //                         </button>
    //                     </>
    //                 )}
    //             </>
    //         )}
    //     </div>
    // );


    return (
        <div className="container p-4">


            <div className="d-flex gap-3 mb-4">
                <button
                    className="btn btn-success"
                    onClick={() => {
                        setMode("upload");
                        setFile(null);
                        setRecords([]);
                    }}
                >
                    Upload New Users
                </button>
                <a href={sampleNewUserLink} download className="btn btn-outline-primary">
                    📥 Sample Excel for New Users
                </a>

                <button
                    className="btn btn-warning"
                    onClick={() => {
                        setMode("update");
                        setFile(null);
                        setRecords([]);
                    }}
                >
                    Update Existing Users
                </button>
                <a href={sampleUpdateUserLink} download className="btn btn-outline-primary">
                    📥 Sample Excel for Update
                </a>
            </div>

            {mode && (
                <>
                    <div style={{ marginBottom: "2rem", fontSize: "20px" }}>
                        <h3>
                            {mode === "upload"
                                ? "Upload New User"
                                : mode === "update"
                                    ? "Update Old User Selected"
                                    : "Select an Action"}
                            <span className="ms-2 text-primary">{mode ? `(${mode})` : ""}</span>
                        </h3>
                    </div>
                    <div className="mb-3">
                        <input type="file" accept=".xls,.xlsx,.csv" onChange={handleFileChange} />
                    </div>

                    {records.length > 0 && (
                        <>
                            <div className="table-responsive mb-3">
                                <table className="table table-bordered table-sm">
                                    <thead>
                                        <tr>
                                            {Object.keys(records[0]).map((key) => (
                                                <th key={key}>{key}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {records.map((row, idx) => (
                                            <tr key={idx}>
                                                {Object.values(row).map((value, i) => (
                                                    <td key={i}>
                                                        {value instanceof Date
                                                            ? value.toLocaleDateString("en-IN")
                                                            : String(value ?? "")}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <button
                                className="btn btn-primary"
                                onClick={mode === "upload" ? handleUpload : handleUpdate}
                                disabled={uploading}
                            >
                                {uploading
                                    ? mode === "upload" ? "Uploading..." : "Updating..."
                                    : mode === "upload" ? "Submit New Users" : "Update Users"}
                            </button>
                        </>
                    )}
                </>
            )}
        </div>
    );


}




export default BatchUploadUsers;


// import React, { useState } from "react";
// import * as XLSX from "xlsx";
// import {
//     getFirestore, collection, writeBatch, doc, setDoc
// } from "firebase/firestore";
// import { getAuth } from "firebase/auth";
// import Swal from "sweetalert2";
// import { Timestamp } from "firebase/firestore";
// import db from "./Firebase"; // your firebase init file

// function BatchUploadUsers() {
//     const [file, setFile] = useState(null);
//     const [records, setRecords] = useState([]);
//     const [uploading, setUploading] = useState(false);

//     const handleFileChange = (e) => {
//         const f = e.target.files[0];
//         setFile(f);

//         const reader = new FileReader();
//         reader.onload = (evt) => {
//             const data = new Uint8Array(evt.target.result);
//             const workbook = XLSX.read(data, { type: "array" });
//             const sheet = workbook.Sheets[workbook.SheetNames[0]];
//             const rows = XLSX.utils.sheet_to_json(sheet);
//             setRecords(rows);
//         };
//         reader.readAsArrayBuffer(f);
//     };

//     const handleUpload = async () => {
//         if (!records.length) {
//             Swal.fire("Error", "No records to upload", "error");
//             return;
//         }
//         setUploading(true);
//         const dbInstance = getFirestore(); // or use your `db`
//         const batch = writeBatch(dbInstance);

//         let successCount = 0;
//         let failCount = 0;

//         for (let r of records) {
//             try {
//                 const phone = String(
//                     r["phone_no"] || r["Phone No"] || r["Phone"] || r["phone"] || ""
//                 ).trim();
//                 if (!phone) {
//                     console.log("Row keys:", Object.keys(r));
//                     console.log("Row phone:", r["phone_no"], r["phone"]);
//                     failCount++;
//                     continue;
//                 }

//                 // Prepare data objects
//                 const funderData = {
//                     phone_no: phone,
//                     name: r["name"] || "",
//                     username: r["username"] || "",
//                     state: r["state"] || "",
//                     city: r["city"] || "",
//                     type: r["type"] || "",
//                     age_range: r["age"] || "",
//                     end_date: Timestamp.fromDate(new Date("2025-12-15T10:30:00")),
//                     funder: 'nascom',
//                     phase: '1',
//                     start_date: Timestamp.fromDate(new Date("2025-10-03T10:30:00")),
//                 };

//                 const featureData = {
//                     end_date: Timestamp.fromDate(new Date("2025-12-15T10:30:00")),
//                     funder: funderData.funder,
//                     phase: `phase ${funderData.phase || "1"}`,
//                     phone: phone,
//                     start_date: Timestamp.fromDate(new Date("2025-10-03T10:30:00")),
//                     status: "1"
//                 };

//                 // Document references
//                 const docRefUsers = doc(collection(dbInstance, "funder_users_list"), phone);
//                 const docRefFeatures = doc(collection(dbInstance, "udhyogini_features"), phone);

//                 batch.set(docRefUsers, funderData, { merge: true });
//                 batch.set(docRefFeatures, featureData, { merge: true });

//                 successCount++;
//             } catch (err) {
//                 console.error("Batch row error:", err, r);
//                 failCount++;
//             }
//         }

//         try {
//             await batch.commit();
//             Swal.fire("Success", `Uploaded ${successCount} records, failed ${failCount}`, "success");
//         } catch (err) {
//             console.error("Batch commit error:", err);
//             Swal.fire("Error", "Batch upload failed", "error");
//         } finally {
//             setUploading(false);
//         }
//     };

//     return (
//         <div className="container p-4">
//             <h3>Upload Users via Excel</h3>
//             <div className="mb-3">
//                 <input type="file" accept=".xls,.xlsx,.csv" onChange={handleFileChange} />
//             </div>
//             {records.length > 0 && (
//                 <div className="mb-3">
//                     <p>{records.length} records ready to upload</p>
//                     <button
//                         className="btn btn-primary"
//                         onClick={handleUpload}
//                         disabled={uploading}
//                     >
//                         {uploading ? "Uploading..." : "Upload All"}
//                     </button>
//                 </div>
//             )}
//         </div>
//     );
// }

// export default BatchUploadUsers;
